import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { DeviceDetectorService } from '../../shared/services/device-detector/device-detector.service';
import {
  DialogPosition,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
  MatDialogState
} from '@angular/material/dialog';
import { PanelService } from '../../shared/services/api/panel.service';
import { StoreRegionService } from '../../shared/services/store-region.service';
import { Url_Friendly_String } from '../../shared/helpers/helper';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SearchSuggestionViewComponent } from '../search-suggestion-view/search-suggestion-view.component';
import { SearchBriefResultComponent } from '../search-brief-result/search-brief-result.component';
import { MatButtonModule } from '@angular/material/button';
import { ClickOutsideElementDirective } from '../../shared/directive/click-outside-element.directive';

@Component({
    selector: 'shared-search-field',
    templateUrl: './search-field.component.html',
    styleUrls: ['./search-field.component.scss'],
    standalone: true,
    imports: [ClickOutsideElementDirective, NgClass, MatInput, FormsModule, ReactiveFormsModule, MatProgressSpinner, SearchSuggestionViewComponent, SearchBriefResultComponent, MatButtonModule, MatDialogModule]
})
export class SearchFieldComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private deviceDetectorService = inject(DeviceDetectorService);
  private dialog = inject(MatDialog);
  private panelService = inject(PanelService);
  private regionService = inject(StoreRegionService);
  private platformId = inject(PLATFORM_ID);
  searchState = {
    IDLE: 'idle',
    SEARCHING: 'searching',
    NAVIGATING: 'navigating'
  };

  @Input() opened: boolean;
  @Output() onFocus = new EventEmitter<any>();
  @ViewChild('searchDialog') searchDialog: any;
  @ViewChild('searchFieldContainer') searchFieldContainer: ElementRef;
  @ViewChild('searchInput') searchInput: ElementRef;
  dialogRef: MatDialogRef<any>;
  searchedQuery: string;
  searchField = new FormControl();
  state = this.searchState.IDLE;
  prevRouteTarget: string;
  isDesktop: boolean;
  searchResult: any[];

  constructor() {
    if(isPlatformBrowser(this.platformId)){
      this.deviceDetectorService.$breakpoint().subscribe((sizes) => {
        this.isDesktop = sizes.matches;
        this.closeDialog();
        !this.isDesktop &&
          this.searchField.setValue(decodeURIComponent(this.searchParam), {
            emitEvent: false
          });
      });
    }
  }

  ngOnInit(): void {
    this.initSearchListener();
    this.router.events
      .pipe(
        filter((event) => {
          return event instanceof NavigationEnd;
        })
      )
      .subscribe((res: NavigationEnd) => {
        if (res.urlAfterRedirects.includes('profile')) {
          this.searchFieldContainer?.nativeElement.classList.add('blue-bg');
        } else {
          this.searchFieldContainer?.nativeElement.classList.remove('blue-bg');
        }
      });
  }

  ngAfterViewInit(): void {
    const route: string = this.router.url;
    if (route.includes('profile')) {
      this.searchFieldContainer?.nativeElement.classList.add('blue-bg');
    }
  }

  goToDetails() {
    this.router
      .navigate(['/search'], {
        queryParams: { keyword: this.searchField.value }
      })
      .then((status) => {
        this.closeDialog();
      });
  }

  private get searchParam() {
    let param = '';
    const urlTree = this.router.url.split('/');
    if (urlTree[1] && urlTree[1] === 'search' && urlTree[2]) {
      param = urlTree[2];
    }
    return param;
  }

  private initSearchListener() {
    this.searchField.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        this.onSearchSubmit();
      });
  }

  private setSearchParam(query: string) {
    this.state = this.searchState.SEARCHING;
    this.router
      .navigate([`/search`], { queryParams: { keyword: query } })
      .then((status) => {
        this.state = this.searchState.IDLE;
        this.focusSearchInput();
      });
  }

  private focusSearchInput() {
    this.searchInput.nativeElement.focus();
  }

  onFocusSearchField() {
    if (this.isDesktop) {
      if (!this.opened) {
        this.onFocus.emit(true);
        setTimeout(() => {
          this.openDialog();
        }, 300);
      }
;
      return;
    }

    const searchRoute = '/search';
    if (this.router.url.includes(searchRoute)) return;
    this.router.navigateByUrl(searchRoute).then((status) => {
      this.focusSearchInput();
    });
  }

  backToPrevRoute() {
    if (this.isDesktop) {
      this.closeDialog();
      return;
    }

    this.clearSearch(false);
    this.router.navigateByUrl('/');
  }

  onSearchSubmit() {
    const keyword = this.searchField.value?.trim();
    if (keyword.length >= 2) {
      this.searchedQuery = keyword;
      if (this.isDesktop) {
        this.performSearch(this.searchedQuery);
      } else {
        this.setSearchParam(this.searchedQuery);
      }
    } else if (keyword.length === 0) {
      this.searchedQuery = '';
      if (!this.isDesktop) {
        this.router.navigateByUrl('/search');
      } else {
        this.searchResult = [];
      }
    }
  }

  private performSearch(query: string) {
    this.panelService
      .searchProductByName(query, this.regionService.selectedRegion.id)
      .subscribe((res) => {
        this.searchResult = res.results.map((item) => ({
          ...item,
          url: this.getUrl(item.id, item.name)
        }));
      });
  }

  private getUrl(id: number, name: string) {
    return `/product/${id}/${Url_Friendly_String(name)}`;
  }

  clearSearch(emitEvent: boolean = true) {
    this.searchField.setValue('', { emitEvent });
  }

  private get dialogPositions(): DialogPosition {
    const { offsetTop, offsetHeight, offsetLeft } = this.searchFieldContainer
      ?.nativeElement as HTMLElement;
    const suffix = 'px';
    const marginTop = 10;

    return {
      top: offsetTop + offsetHeight + marginTop + suffix,
      left: offsetLeft + suffix
    };
  }

  openDialog() {
    this.closeDialog();
      this.dialogRef = this.dialog.open(this.searchDialog, {
        autoFocus: false,
        restoreFocus: false,
        position: this.dialogPositions,
        width: this.searchFieldContainer?.nativeElement.offsetWidth,
        minHeight: 'auto',
        panelClass: ['desktop-search-dialog'],
        hasBackdrop: false
      });
      this.dialogRef.afterOpened().subscribe((data) => {
        setTimeout(() => {
          
          this.focusSearchInput();
        }, 0);
      });
      this.dialogRef.afterClosed().subscribe((data) => {
        this.onFocus.emit(false);
        this.clearSearch(false);
        this.searchResult = undefined;
      });
  }

  private closeDialog() {
    if (this.dialogRef && this.dialogRef.getState() !== MatDialogState.CLOSED) {
      this.dialogRef.close();
    }
  }

  handleClickOutside(target: HTMLElement) {
    if (!this.isDesktop || !this.opened || !this.dialogRef) return;

    const dialogElement =
      this.dialogRef['_containerInstance']['_elementRef'].nativeElement;
    const clickedInsideDialog = dialogElement.contains(target);
    if (!clickedInsideDialog) {
      this.closeDialog();
    }
  }
}
